// M15 — locale registry + default-locale resolution (CONTRACT §3, §4).
//
// `LOCALES` is the frozen list of all 24 with native labels (NO flags — language
// is identified by native name). `RTL_LOCALES` is `["ar"]` only.
// `COUNTRY_RECOMMENDED` maps an uppercase ISO country (from the existing
// /api/location/guess cf) to the picker's top-3 recommended locales.
// `resolveDefaultLocale` implements selection: persisted → navigator match
// (full tag, then base; zh* → zh-Hans) → "ru".

import type { Locale } from "./types";

/**
 * localStorage key persisting the user's locale choice. Shared by the main app
 * (`I18nProvider`) and the /lazecast landing (`LandingI18nProvider`) so the
 * language selection stays synchronized across both surfaces. Lives here (the
 * dict-free registry module) because the landing must not import provider
 * runtime — that would drag the app's dicts into the landing chunk. This module
 * staying dict-free is also what lets main.tsx resolve the active locale before
 * mount (M33) without pulling any dictionary into the shared entry chunk.
 */
export const LOCALE_STORAGE_KEY = "lazecast.locale";

export interface LocaleEntry {
  code: Locale;
  native: string;
  rtl: boolean;
}

/**
 * All 24 locales, native labels per CONTRACT §4.
 *
 * M15.2 ORDERING RULE (manual-list / "Все языки" order):
 *   - Ukrainian (`uk`) is ALWAYS FIRST.
 *   - Russian (`ru`) is ALWAYS LAST.
 *   - The middle order is the pre-M15.2 registry order, minus uk/ru.
 * This is a PRESENTATION rule only. `resolveDefaultLocale` (below) is
 * unchanged: the active default is still the browser-locale match, falling
 * back to `ru` for unsupported browsers — `ru` stays a first-class locale.
 */
export const LOCALES: LocaleEntry[] = [
  { code: "uk", native: "Українська", rtl: false },
  { code: "en", native: "English", rtl: false },
  { code: "az", native: "Azərbaycan dili", rtl: false },
  { code: "bg", native: "Български", rtl: false },
  { code: "de", native: "Deutsch", rtl: false },
  { code: "nl", native: "Nederlands", rtl: false },
  { code: "ro", native: "Română", rtl: false },
  { code: "zh-Hans", native: "中文（简体）", rtl: false },
  { code: "pt-PT", native: "Português", rtl: false },
  { code: "es", native: "Español", rtl: false },
  { code: "it", native: "Italiano", rtl: false },
  { code: "el", native: "Ελληνικά", rtl: false },
  { code: "tr", native: "Türkçe", rtl: false },
  { code: "ar", native: "العربية", rtl: true },
  { code: "hu", native: "Magyar", rtl: false },
  { code: "fr", native: "Français", rtl: false },
  { code: "pl", native: "Polski", rtl: false },
  { code: "ca", native: "Català", rtl: false },
  { code: "ms", native: "Bahasa Melayu", rtl: false },
  { code: "hi", native: "हिन्दी", rtl: false },
  { code: "sq", native: "Shqip", rtl: false },
  { code: "tt", native: "Татарча", rtl: false },
  { code: "yue", native: "粵語", rtl: false },
  { code: "ru", native: "Русский", rtl: false },
];

/** RTL locales (CONTRACT §1.5, §4) — only Arabic. */
export const RTL_LOCALES: Locale[] = ["ar"];

/** Set of all valid locale codes (fast membership test). */
const LOCALE_SET = new Set<string>(LOCALES.map((l) => l.code));

/** The final default when nothing else matches (CONTRACT §3). */
export const DEFAULT_LOCALE: Locale = "ru";

export function isLocale(value: string): value is Locale {
  return LOCALE_SET.has(value);
}

export function localeDir(locale: Locale): "ltr" | "rtl" {
  return RTL_LOCALES.includes(locale) ? "rtl" : "ltr";
}

/**
 * Country → recommended top-3 locales for picker ordering (CONTRACT §4). The
 * country comes from the existing /api/location/guess cf data; only registry
 * locales appear (out-of-registry suggestions already degraded to en/nearest).
 */
export const COUNTRY_RECOMMENDED: Record<string, Locale[]> = {
  // M15.2: UA → uk FIRST. `ru` is intentionally NOT listed here — it must never
  // sit above `uk`. Russian stays available via the full «Все языки» list (where
  // it renders LAST). `en` is the practical second choice.
  UA: ["uk", "en"],
  AZ: ["az", "ru", "en"],
  BG: ["bg", "en", "tr"],
  CA: ["en", "fr", "zh-Hans"],
  DE: ["de", "en", "tr"],
  NL: ["nl", "en", "ar"],
  RO: ["ro", "en", "hu"],
  // M15.2: RU keeps its local-language picker group, but the suggestion card
  // never PROMOTES `ru` (manual choice is kept). `ru` remains the browser
  // default via resolveDefaultLocale for ru browsers; see LanguageSuggestion,
  // which skips a `ru` suggestion so we never force-switch into Russian.
  RU: ["ru", "en", "tt"],
  SG: ["en", "zh-Hans", "ms"],
  CH: ["de", "fr", "it"],
  GB: ["en", "pl", "ro"],
  US: ["en", "es", "zh-Hans"],
  CN: ["zh-Hans", "en", "yue"],
  PT: ["pt-PT", "en", "es"],
  ES: ["es", "ca", "en"],
  IT: ["it", "en", "de"],
  GR: ["el", "en", "sq"],
  TR: ["tr", "en", "ar"],
  AE: ["ar", "en", "hi"],
  HU: ["hu", "en", "de"],
};

/**
 * Map a single `navigator.languages` tag to a registry locale, or null.
 *   - exact tag match (`pt-PT`, `zh-Hans`) wins;
 *   - then base language (`pt` → first registry locale whose base is `pt`);
 *   - `zh*` always resolves to `zh-Hans` (CONTRACT §3);
 *   - `nb` / `nn` are skipped (return null).
 */
function matchNavigatorTag(tag: string): Locale | null {
  if (!tag) return null;
  const normalized = tag.trim();
  if (!normalized) return null;

  // Exact tag (case-insensitive on the language subtag, region kept as-is).
  for (const entry of LOCALES) {
    if (entry.code.toLowerCase() === normalized.toLowerCase()) return entry.code;
  }

  const base = (normalized.split("-")[0] ?? normalized).toLowerCase();

  // Norwegian Bokmål / Nynorsk are intentionally skipped (no registry entry).
  if (base === "nb" || base === "nn") return null;

  // Any Chinese variant collapses to Simplified.
  if (base === "zh") return "zh-Hans";

  // Base-language match against a registry locale's base.
  for (const entry of LOCALES) {
    if ((entry.code.split("-")[0] ?? entry.code).toLowerCase() === base)
      return entry.code;
  }

  return null;
}

/**
 * Resolve the active default locale (CONTRACT §3):
 *   persisted choice → first supported `navigator.languages` match → "ru".
 */
export function resolveDefaultLocale(
  persisted: string | null | undefined,
  navigatorLanguages: readonly string[] | undefined,
): Locale {
  if (persisted && isLocale(persisted)) return persisted;

  for (const tag of navigatorLanguages ?? []) {
    const matched = matchNavigatorTag(tag);
    if (matched) return matched;
  }

  return DEFAULT_LOCALE;
}
